/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import Vue from 'vue';
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { modifiers as m } from 'vue-tsx-support';
import { string } from '@codegrade/utils';
import * as utils from '@/utils';
import './style.less';
const EVENT_EXPAND = 'cg::expand::collapse';
const EVENT_COLLAPSE = 'cg::collapse::collapse';
const EVENT_TOGGLE = 'cg::toggle::collapse';
const EVENT_STATE = 'cg::toggle::state';
const BVT = '__CG_toggle__';
Vue.directive('cg-toggle', {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    bind(el, binding, vnode) {
        const target = binding.value;
        if (typeof target !== 'string' || target === '') {
            // eslint-disable-next-line
            console.warn('Target given not found!');
        }
        if (vnode.elm != null) {
            vnode.elm.addEventListener('click', () => {
                vnode.context?.$root.$emit(EVENT_TOGGLE, target);
            });
            if (!(vnode.elm instanceof HTMLElement) || vnode.elm.tagName !== 'BUTTON') {
                vnode.elm.addEventListener('keyup', event => {
                    if (event instanceof KeyboardEvent && event.key === 'Enter') {
                        vnode.context?.$root.$emit(EVENT_TOGGLE, target);
                    }
                });
            }
        }
        el.setAttribute('tabindex', '0');
        el.setAttribute('aria-controls', target);
        el.setAttribute('aria-expanded', 'false');
        if (el.tagName !== 'BUTTON') {
            // If element is not a button, we add `role="button"` for accessibility
            el.setAttribute('role', 'button');
        }
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        function toggleDirectiveHandler(id, state) {
            if (target != null && target === id) {
                // Set aria-expanded state
                el.setAttribute('aria-expanded', state ? 'true' : 'false');
                // Set/Clear 'collapsed' class state
                if (state) {
                    el.classList.remove('collapsed');
                    el.classList.remove('x-collapsed');
                }
                else {
                    el.classList.add('collapsed');
                    el.classList.add('x-collapsed');
                }
            }
        }
        el[BVT] = toggleDirectiveHandler;
        vnode.context?.$root.$on(EVENT_STATE, toggleDirectiveHandler);
    },
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    unbind(el, _, vnode) {
        const e = el;
        if (e[BVT] != null) {
            // Remove our $root listener
            vnode.context?.$root.$off(EVENT_STATE, e[BVT]);
            e[BVT] = null;
        }
    },
});
export default tsx.componentFactoryOf().create({
    name: 'collapse',
    model: {
        prop: 'collapsed',
        event: 'change',
    },
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    created() {
        this.$root.$on(EVENT_EXPAND, this.handleExpandEvent);
        this.$root.$on(EVENT_COLLAPSE, this.handleCollapseEvent);
        this.$root.$on(EVENT_TOGGLE, this.handleToggleEvent);
    },
    /** Emit the initial state. */
    mounted() {
        if (!this.noEmitOnMounted) {
            this.emitState();
        }
    },
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    beforeDestroy() {
        this.$root.$off(EVENT_EXPAND, this.handleExpandEvent);
        this.$root.$off(EVENT_COLLAPSE, this.handleCollapseEvent);
        this.$root.$off(EVENT_TOGGLE, this.handleToggleEvent);
    },
    props: {
        id: p(String, Number).optional,
        collapsed: p(Boolean).default(true),
        disabled: p(Boolean).default(false),
        speed: p(Number).default(1000),
        minDuration: p(Number).default(250),
        maxDuration: p(Number).default(750),
        wrapperTag: p.ofType().default('div'),
        handleTag: p.ofType().default('div'),
        contentTag: p.ofType().default('div'),
        lazyLoad: p(Boolean).default(false),
        lazyLoadAlways: p(Boolean).default(false),
        noAnimation: p(Boolean).default(false),
        /**
         * Do not emit the initial state after the component has been mounted.
         *
         * Some old use cases of this component depend on this component
         * immediately emitting its state, but usually it should not be
         * necessary.
         */
        noEmitOnMounted: p(Boolean).default(false),
    },
    /** @returns The initial runtime data of the component. */
    data() {
        const uniqueId = utils.getUniqueId();
        return {
            uniqueId,
            state: this.collapsed ? 'collapsed' : 'expanded',
            contentStyle: {
                height: this.collapsed ? '0px' : 'auto',
            },
            loaded: (!this.lazyLoadAlways && !this.lazyLoad) || !this.collapsed,
            show: !this.collapsed,
            transitionId: null,
        };
    },
    computed: {
        /**
         * @returns Whether the content slot of the collapse should be rendered.
         * We should _not_ render the content in some cases if `lazyLoad` or
         * `lazyLoadAlways` are set.
         */
        shouldRenderContent() {
            if (this.loaded) {
                return true;
            }
            if (this.lazyLoadAlways) {
                // We need to check both `this.show` and `this.state` here:
                // `this.show` is set to false as soon as the component enters
                // the `collapsing` state, but the content needs to stay visible
                // until we have entered the `collapsed` state, otherwise the
                // collapsing animation is skipped.
                return this.show || this.state !== 'collapsed';
            }
            return false;
        },
        /** @returns The id of the content wrapper element. */
        contentWrapperId() {
            return `content-wrapper-${this.uniqueId}`;
        },
    },
    watch: {
        /** Called when the collapsed prop is changed. */
        collapsed() {
            this.show = !this.collapsed;
        },
        /** Called when `show` changes. */
        show() {
            this.emitState();
            this.toggle();
        },
    },
    methods: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        handleExpandEvent(target) {
            this.handleEvent(target, true);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        handleCollapseEvent(target) {
            this.handleEvent(target, false);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        handleToggleEvent(target) {
            this.handleEvent(target, !this.show);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        handleEvent(target, show) {
            if (this.id == null || target !== this.id || this.disabled) {
                return;
            }
            this.show = show;
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        emitState() {
            tsx.emitOn(this, 'onChange', !this.show);
            this.$root.$emit(EVENT_STATE, this.id, this.show);
        },
        /**
         * Toggles the show property if the collapse is not disabled,
         * and emits the user toggle event.
         */
        onClick() {
            if (!this.disabled) {
                this.show = !this.show;
                tsx.emitOn(this, 'onUserToggle', !this.show);
            }
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        setState(name) {
            this.state = name;
            tsx.emitOn(this, `on${string.capitalize(name)}`);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async toggle() {
            if (!this.loaded && !this.lazyLoadAlways) {
                this.loaded = true;
                await this.$afterRerender();
            }
            if (this.noAnimation) {
                this.toggleWithoutAnimation();
                return;
            }
            const wrapperEl = await this.$waitForRef('content');
            if (!(wrapperEl instanceof Element)) {
                return;
            }
            const contentEl = wrapperEl.firstElementChild;
            if (contentEl == null) {
                return;
            }
            const wrapperHeight = this.getHeight(wrapperEl);
            const contentHeight = this.getHeight(contentEl);
            const newHeight = this.show ? contentHeight : 0;
            const duration = this.getDuration(contentHeight);
            const transitionId = `${utils.getUniqueId()}`;
            if (contentHeight === 0) {
                // Collapse is not visible, so don't bother animating.
                this.toggleWithoutAnimation();
                return;
            }
            this.setState(newHeight > 0 ? 'expanding' : 'collapsing');
            this.contentStyle = {
                height: `${wrapperHeight}px`,
                transitionDuration: `${duration}ms`,
            };
            // Wait until style has been applied.
            await this.$afterRerender();
            this.contentStyle = {
                height: `${newHeight}px`,
                transitionDuration: `${duration}ms`,
            };
            this.transitionId = transitionId;
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            const onTransitionEnd = () => {
                wrapperEl.removeEventListener('transitionend', onTransitionEnd);
                if (this.transitionId === transitionId) {
                    this.setState(newHeight > 0 ? 'expanded' : 'collapsed');
                    this.contentStyle = {
                        height: newHeight > 0 ? 'auto' : '0px',
                    };
                }
            };
            wrapperEl.addEventListener('transitionend', onTransitionEnd);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        toggleWithoutAnimation() {
            this.setState(this.show ? 'expanded' : 'collapsed');
            this.contentStyle = { height: this.show ? 'auto' : '0' };
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        expand() {
            this.show = true;
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        collapse() {
            this.show = false;
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        getHeight(el) {
            const s = getComputedStyle(el);
            return [
                el.clientHeight,
                parseInt(s.marginTop, 10),
                parseInt(s.marginBottom, 10),
            ].reduce((x, y) => x + y);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        getDuration(height) {
            let duration = (1000 * height) / this.speed;
            duration = Math.min(duration, this.maxDuration);
            duration = Math.max(duration, this.minDuration);
            return duration;
        },
    },
    /** @returns The rendered component. */
    render(h) {
        const children = [];
        if (this.$scopedSlots.handle != null) {
            children.push(h(this.handleTag, {
                class: { handle: true, disabled: this.disabled },
                on: {
                    click: m.stop(this.onClick),
                    keyup: m.stop.enter(this.onClick),
                },
                attrs: {
                    role: 'button',
                    tabindex: 0,
                    'aria-label': this.show ? 'Collapse the content' : 'Expand the content',
                    'aria-controls': this.contentWrapperId,
                },
            }, [this.$scopedSlots.handle?.()]));
        }
        if (this.shouldRenderContent) {
            children.push(h(this.contentTag, {
                class: 'content-wrapper',
                style: this.contentStyle,
                ref: 'content',
                attrs: {
                    id: this.contentWrapperId,
                    'aria-hidden': !this.show,
                    tabindex: this.show ? 0 : undefined,
                },
            }, [h('div', { class: 'content' }, [this.$scopedSlots.default?.()])]));
        }
        return h(this.wrapperTag, {
            attrs: {
                id: this.id,
                'aria-expanded': this.show,
            },
            class: ['x-collapse', `x-${this.state}`],
        }, children);
    },
});
